/**
 * Quota limits and calculation for billing tiers
 */
/**
 * Tier quota definitions by plan.
 *
 * Every number here is a product decision with no recorded rationale: the
 * table was written into task 57's spec as it stands, and earlier specs carried
 * different placeholder values. No cost model or measurement sits behind it.
 * The marketing site's pricing and the onboarding tier cards read their limits
 * from here (task 278), and `apps/landing/tests/expected-plans.ts` is compared
 * with it, so changing a number fails that table until a human re-reads what
 * the site will publish. Prices are not here: what a plan costs is its Stripe
 * price, and a copy of it in this table was read by nothing (task 278 removed
 * it). See `docs/guides/constant-provenance.md`.
 */
export const TIER_QUOTAS = {
    free: {
        monthlyReportLimit: 10_000,
        softLimitMultiplier: 3,
        siteLimit: 3,
        retentionDays: 15,
        userLimit: 1,
    },
    team: {
        monthlyReportLimit: 100_000,
        softLimitMultiplier: 3,
        siteLimit: 10,
        retentionDays: 30,
        userLimit: 10,
    },
    professional: {
        monthlyReportLimit: 250_000,
        softLimitMultiplier: 3,
        siteLimit: 25,
        retentionDays: 60,
        userLimit: 50,
    },
    business: {
        monthlyReportLimit: 750_000,
        softLimitMultiplier: 3,
        siteLimit: 100,
        retentionDays: 60,
        userLimit: 100,
    },
};
/**
 * Length of the Team plan's free trial, in days. `checkout.ts` passes it to
 * Stripe as `trial_period_days`, and every page that states the trial reads it
 * from here rather than writing the number (task 278).
 */
export const TEAM_TRIAL_DAYS = 30;
/**
 * The most sites any plan allows: the largest number of its own sites a tenant
 * can hold, and so the most it can ask to filter by at once (task 277).
 * Derived rather than restated, so a plan that raises its `siteLimit` raises
 * this with it.
 */
export const MAX_PLAN_SITE_LIMIT = Math.max(...Object.values(TIER_QUOTAS).map((quota) => quota.siteLimit));
/**
 * Get quota configuration for a plan
 */
export function getQuotaForPlan(plan) {
    return TIER_QUOTAS[plan];
}
/**
 * Calculate the hard limit (soft limit * multiplier)
 */
export function getHardLimit(quota) {
    return quota.monthlyReportLimit * quota.softLimitMultiplier;
}
/**
 * Determine quota status based on current usage
 */
export function getQuotaStatus(current, quota) {
    if (current < quota.monthlyReportLimit) {
        return "under";
    }
    const hardLimit = getHardLimit(quota);
    if (current >= hardLimit) {
        return "hard_exceeded";
    }
    return "soft_exceeded";
}
/**
 * Share of the monthly limit used, as a whole percentage (0-100+).
 *
 * Floored, not rounded, so the figure a customer sees crosses a threshold at
 * the same report the status does: 79.5% reads as 79, never as an "80%" that
 * is still `under` the 80% warning, and 99.5% reads as 99 rather than a "100%"
 * that is not yet over the limit (task 278).
 */
export function getPercentUsed(current, quota) {
    // Multiplied before dividing: `(2_900 / 10_000) * 100` is 28.999…, which
    // floors to 28, while an integer over the limit is exact enough to floor.
    return Math.floor((current * 100) / quota.monthlyReportLimit);
}
/**
 * Calculate billing period boundaries
 *
 * For tenants with Stripe subscriptions, uses currentPeriodStart from subscription.
 * For free tier, uses calendar month (first day of current month, 00:00:00 UTC).
 */
export function calculatePeriodBounds(subscription) {
    if (subscription) {
        return {
            start: new Date(subscription.currentPeriodStart),
            end: new Date(subscription.currentPeriodEnd),
        };
    }
    // Free tier: use calendar month
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    return { start, end };
}
//# sourceMappingURL=limits.js.map